/**
 * Phone number parsing, validation, and E.164 normalization (Twilio-compatible).
 */

import {
  parsePhoneNumberFromString,
  formatIncompletePhoneNumber,
  getCountries,
  getCountryCallingCode,
  type CountryCode,
} from 'libphonenumber-js';
import { getMarketConfig } from '../constants/markets';
import type { Region } from '../types/region';

export const PINNED_PHONE_COUNTRIES = ['US', 'AU', 'GB'] as const;

export type ParsedPhone = {
  country: CountryCode;
  nationalNumber: string;
  e164: string;
};

export type DetectDefaultPhoneCountryOptions = {
  /** ISO 3166-1 alpha-2 country code from academy or profile */
  countryCode?: string | null;
  /** CourtSync region — mapped via MARKET_CONFIG */
  region?: Region | null;
  /** Browser locale, e.g. en-AU */
  locale?: string | null;
  /** IANA timezone, e.g. Australia/Sydney */
  timezone?: string | null;
};

function toCountryCode(value: string | null | undefined): CountryCode | null {
  if (!value || typeof value !== 'string') return null;
  const upper = value.trim().toUpperCase();
  if (upper.length !== 2) return null;
  const countries = getCountries();
  return countries.includes(upper as CountryCode) ? (upper as CountryCode) : null;
}

function regionToCountryCode(region: Region | null | undefined): CountryCode | null {
  if (!region) return null;
  const market = getMarketConfig(region);
  if (!market?.countryCode || market.countryCode === 'EU') return null;
  return toCountryCode(market.countryCode);
}

function detectCountryFromTimezone(timezone: string): CountryCode | null {
  const tz = timezone.toLowerCase();
  if (
    tz.includes('australia') ||
    tz.includes('sydney') ||
    tz.includes('melbourne') ||
    tz.includes('brisbane') ||
    tz.includes('perth') ||
    tz.includes('adelaide') ||
    tz.includes('darwin') ||
    tz.includes('hobart')
  ) {
    return 'AU';
  }
  if (
    tz.includes('america') ||
    tz.includes('new_york') ||
    tz.includes('los_angeles') ||
    tz.includes('chicago') ||
    tz.includes('denver') ||
    tz.includes('phoenix')
  ) {
    return 'US';
  }
  if (tz.includes('london') || tz.includes('europe/london')) {
    return 'GB';
  }
  return null;
}

function detectCountryFromLocale(locale: string): CountryCode | null {
  const lower = locale.toLowerCase();
  try {
    const intlLocale = new Intl.Locale(locale);
    const region = intlLocale.region;
    if (region) {
      const fromRegion = toCountryCode(region);
      if (fromRegion) return fromRegion;
    }
  } catch {
    // Intl.Locale unsupported or invalid — fall through
  }
  if (lower.includes('au') || lower.includes('australia') || lower.includes('en-au')) {
    return 'AU';
  }
  if (lower.includes('gb') || lower.includes('uk') || lower.includes('en-gb')) {
    return 'GB';
  }
  if (lower.includes('us') || lower.includes('en-us')) {
    return 'US';
  }
  return null;
}

/**
 * Resolve default ISO country for phone input selector.
 */
export function detectDefaultPhoneCountry(
  options: DetectDefaultPhoneCountryOptions = {},
): CountryCode {
  const fromExplicit = toCountryCode(options.countryCode);
  if (fromExplicit) return fromExplicit;

  const fromRegion = regionToCountryCode(options.region ?? null);
  if (fromRegion) return fromRegion;

  if (options.locale) {
    const fromLocale = detectCountryFromLocale(options.locale);
    if (fromLocale) return fromLocale;
  }

  if (options.timezone) {
    const fromTz = detectCountryFromTimezone(options.timezone);
    if (fromTz) return fromTz;
  }

  return 'US';
}

/**
 * Detect default country in browser environments.
 */
export function detectDefaultPhoneCountryInBrowser(
  options: Omit<DetectDefaultPhoneCountryOptions, 'locale' | 'timezone'> = {},
): CountryCode {
  let locale: string | undefined;
  let timezone: string | undefined;
  if (typeof navigator !== 'undefined') {
    locale = navigator.language || navigator.languages?.[0];
  }
  if (typeof Intl !== 'undefined') {
    try {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      timezone = undefined;
    }
  }
  return detectDefaultPhoneCountry({ ...options, locale, timezone });
}

export function getPhoneCountryCallingCode(country: CountryCode): string {
  return `+${getCountryCallingCode(country)}`;
}

export function sanitizeNationalPhoneInput(value: string): string {
  return value.replace(/\D/g, '');
}

/** Digits-only form for matching stored phones (E.164 or legacy formats). */
export function phoneDigitsOnly(value: string | null | undefined): string {
  if (!value) return '';
  return value.replace(/\D/g, '');
}

/**
 * Parse phone input into country, national digits, and E.164.
 */
export function parsePhone(
  value: string | null | undefined,
  defaultCountry: CountryCode = 'US',
): ParsedPhone | null {
  if (!value?.trim()) return null;

  const parsed = parsePhoneNumberFromString(value.trim(), defaultCountry);
  if (!parsed || !parsed.isValid()) return null;

  return {
    country: parsed.country ?? defaultCountry,
    nationalNumber: parsed.nationalNumber,
    e164: parsed.format('E.164'),
  };
}

/**
 * Normalize any phone string to E.164, or null if invalid/empty.
 */
export function normalizePhoneToE164(
  value: string | null | undefined,
  defaultCountry: CountryCode = 'US',
): string | null {
  const parsed = parsePhone(value, defaultCountry);
  return parsed?.e164 ?? null;
}

export function isValidPhone(
  value: string | null | undefined,
  defaultCountry: CountryCode = 'US',
): boolean {
  if (!value?.trim()) return false;
  const parsed = parsePhoneNumberFromString(value.trim(), defaultCountry);
  return parsed?.isValid() ?? false;
}

/**
 * Format E.164 for national display in the input field.
 */
export function formatPhoneNational(
  e164: string | null | undefined,
  defaultCountry: CountryCode = 'US',
): string {
  if (!e164?.trim()) return '';
  const parsed = parsePhoneNumberFromString(e164);
  if (parsed) {
    return parsed.formatNational();
  }
  return formatIncompletePhoneNumber(e164, defaultCountry);
}

/**
 * Split stored E.164 (or legacy formatted value) for controlled input state.
 */
export function splitPhoneForInput(
  value: string | null | undefined,
  defaultCountry: CountryCode = 'US',
): { country: CountryCode; nationalDisplay: string } {
  if (!value?.trim()) {
    return { country: defaultCountry, nationalDisplay: '' };
  }

  const parsed = parsePhoneNumberFromString(value.trim(), defaultCountry);
  if (parsed?.country && parsed.isValid()) {
    return {
      country: parsed.country,
      nationalDisplay: parsed.formatNational(),
    };
  }

  if (parsed?.country) {
    return {
      country: parsed.country,
      nationalDisplay: parsed.nationalNumber,
    };
  }

  const digits = sanitizeNationalPhoneInput(value);
  return { country: defaultCountry, nationalDisplay: digits };
}
